import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Wrench, CalendarDays, CalendarCheck, Clock, Phone, ExternalLink, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScheduleEditor from '../components/fitting/ScheduleEditor';
import CalendarFeedCard from '../components/fitting/CalendarFeedCard';
import PartnerReliabilityCard from '../components/fitting/PartnerReliabilityCard';
import CentrePushCard from '../components/fitting/CentrePushCard';
import PartnerStatementsCard from '../components/fitting/PartnerStatementsCard';
import CentreReplyBox from '../components/fitting/CentreReplyBox';
import { ReviewItem } from '../components/fitting/CentreReviewsList';
import { fittingAPI } from '../services/api';
import { i18n, useT } from '../services/i18n';

const LANG_NAMES = { fr: 'Français', en: 'English', de: 'Deutsch', es: 'Español', it: 'Italiano' };

// Self-service page for a partner centre (personal link by e-mail): shown in the centre's language (centre.lang)
export default function FittingCentreSchedulePage() {
  const { token } = useParams();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  const reload = () => fittingAPI.selfService(token).then((d) => { setData(d); if (d.centre.lang) i18n.set(d.centre.lang); }).catch((e) => setError(e.response?.data?.detail || t('ps.invalidLink')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [token]);

  const save = async (schedule) => {
    setSaving(true); setSaved(''); setError('');
    try { setData(await fittingAPI.selfServiceSchedule(token, schedule)); setSaved(t('ps.savedSchedule')); } catch (e) { setError(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || t('ps.saveFailed')); } finally { setSaving(false); }
  };
  const changeLang = async (lang) => {
    try { await fittingAPI.selfServiceLang(token, lang); setData((d) => ({ ...d, centre: { ...d.centre, lang } })); i18n.set(lang); setSaved(i18n.t('ps.savedLang', { lang: LANG_NAMES[lang] })); } catch { setError(t('ps.langFailed')); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-10" data-testid="centre-schedule-page">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 mb-1"><Wrench className="text-orange-500" /> {t('ps.title')}</h1>
        {error && !data && <p className="text-red-600" data-testid="cs-error">{error}</p>}
        {data && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-6">
              <p className="text-sm text-gray-600 flex-1" data-testid="cs-centre">{data.centre.name} · {data.centre.address}, {data.centre.postcode} {data.centre.city} · {Number(data.centre.price_per_tyre).toFixed(2).replace('.', ',')} € {t('ps.perTyre')}</p>
              <label className="flex items-center gap-2 text-xs text-gray-600" title={t('ps.langHint')}>
                {t('ps.langLabel')}
                <select value={data.centre.lang || 'fr'} data-testid="cs-lang" onChange={(e) => changeLang(e.target.value)} className="h-8 text-sm rounded-md border border-gray-300 bg-white px-2">
                  {Object.entries(LANG_NAMES).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
              </label>
            </div>
            <PartnerReliabilityCard data={data.reliability} />
            <CentrePushCard token={token} />
            <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 mb-1"><CalendarDays size={18} className="text-orange-500" /> {t('ps.slotsTitle')}</h2>
              <p className="text-xs text-gray-500 mb-4">{t('ps.slotsIntro')}</p>
              <ScheduleEditor value={data.schedule} onSave={save} saving={saving} />
              {saved && <p className="mt-3 text-sm text-green-700" data-testid="cs-saved">{saved}</p>}
              {error && <p className="mt-3 text-sm text-red-600" data-testid="cs-error">{String(error)}</p>}
            </section>
            <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 mb-3"><CalendarCheck size={18} className="text-orange-500" /> {t('ps.upcomingTitle')} <span className="text-xs font-normal text-gray-500">({data.upcoming.length})</span></h2>
              {data.upcoming.length === 0 ? <p className="text-sm text-gray-400" data-testid="cs-empty">{t('ps.noUpcoming')}</p> : (
                <ul className="divide-y" data-testid="cs-upcoming">
                  {data.upcoming.map((a) => (
                    <li key={a.order_number} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`cs-appt-${a.order_number}`}>
                      <span><strong>{i18n.formatDateTime(a.date)}</strong> · {a.customer} · {t('ps.nTyres', { n: a.tyres })} <span className="text-gray-400">· {a.order_number}</span>{a.phone && <span className="text-gray-500 inline-flex items-center gap-1 ml-2"><Phone size={11} /> {a.phone}</span>}</span>
                      <span className="inline-flex items-center gap-2">
                        {a.status === 'requested' ? <span className="text-xs text-amber-700 inline-flex items-center gap-1"><Clock size={12} /> {t('ps.toConfirm')}</span> : <span className="text-xs text-green-700 inline-flex items-center gap-1"><CalendarCheck size={12} /> {t('ps.confirmed')}</span>}
                        {a.confirm_url && <a href={a.confirm_url} className="text-xs text-[#1e3a5f] underline inline-flex items-center gap-1" data-testid={`cs-confirm-${a.order_number}`}>{a.status === 'requested' ? t('ps.confirm') : t('ps.edit')} <ExternalLink size={11} /></a>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4"><CalendarFeedCard icalPath={data.ical_path} /></div>
            </section>
            <PartnerStatementsCard token={token} />
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 mb-1"><Star size={18} className="text-amber-500" /> {t('ps.reviewsTitle')} <span className="text-xs font-normal text-gray-500">({data.reviews.length})</span></h2>
              <p className="text-xs text-gray-500 mb-3">{t('ps.reviewsIntro')}</p>
              {data.reviews.length === 0 ? <p className="text-sm text-gray-400" data-testid="cs-reviews-empty">{t('ps.noReviews')}</p> : (
                <ul className="divide-y" data-testid="cs-reviews">
                  {data.reviews.map((r) => <ReviewItem key={r.id} r={r}><CentreReplyBox token={token} review={r} onChanged={reload} /></ReviewItem>)}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
